import { z } from 'zod';
import { ReferralPatientSchema } from '../ReferralPatientSchema';
import { ReferrerSchema } from '../ReferrerSchema';
import { IObject } from '../../IObject';
import { ReferralBaseSchema } from '../ReferralBaseSchema';
import { ReferralLandgCIDocumentsSchema } from './ReferralLandgCIDocumentsSchema';
import { ReferralCoverageSchema } from '../ReferralCoverageSchema';
import { ReferralDiagnosisSchema } from '../ReferralDiagnosisSchema';
import {
  ReferralConsentRequestSchema,
  ReferralConsentSchema,
  requiresPatientPhoneForConsent,
} from '../ReferralConsentSchema';
import { ReferralNotesSchema } from '../ReferralNotesSchema';
import { CARER_JOIN_REASON_ID } from '../../../data/joinReasons';
import { withLegacyPlanIdentifiers } from '../hoistPlanIdentifiersToPatient';

const ReferralBaseLandgCISchemaObject = {
  // ID is required for L&G CI referrals to be passed from the UI because the
  // referrer uploads documents to the storage "referrals/{generated-referral-id}/document.pdf"
  id: z.string().min(1),
  // Member (parent/carer of a child with cancer)
  patient: ReferralPatientSchema,
  coverage: ReferralCoverageSchema,
  diagnosis: ReferralDiagnosisSchema,
  // Referrer (Customer Support / L&G contact)
  referrer: ReferrerSchema,
  // Documents are optional for L&G CI (see schema)
  documents: ReferralLandgCIDocumentsSchema.optional(),
  // Optional free text the referrer typed on the referral form. Internal
  // only: it is never shown to the member (see ReferralNotesSchema)
  notes: ReferralNotesSchema,
  // How we know the patient consented to being referred. The REQUEST form:
  // a body states the EVIDENCE only — the status and what we record when we
  // ask are ours to write (see ReferralConsentSchema; the stored schema
  // below carries the full shape)
  consent: ReferralConsentRequestSchema,
};

// Schema for validating the incoming POST /referrals request.
// joinReasonId is NOT accepted from the UI — L&G CI is always the carers pathway,
// so the server always sets it to "Carer" (2).
export const ReferralCreateLandgCISchema = withLegacyPlanIdentifiers(
  z
    .object(ReferralBaseLandgCISchemaObject)
    .check(requiresPatientPhoneForConsent),
);

// As it is stored in the database
export const ReferralLandgCISchema = ReferralBaseSchema.extend({
  ...ReferralBaseLandgCISchemaObject,
  // as stored: the evidence and status the body stated, plus whatever we
  // recorded when we asked the patient ourselves
  consent: ReferralConsentSchema,
  // L&G CI is always the carers pathway — the join reason is always "Carer".
  joinReasonId: z.literal(CARER_JOIN_REASON_ID).default(CARER_JOIN_REASON_ID),
});

export type ReferralCreateLandgCI = z.infer<typeof ReferralCreateLandgCISchema>;
export type ReferralLandgCIData = z.infer<typeof ReferralLandgCISchema>;
export type ReferralLandgCI = IObject.HasIdTimestamped<ReferralLandgCIData>;
